// // MODIFIED: Explicit separation of Validation and Submission
import React, { useRef, useState } from "react";
import { Input, TextArea } from "../../../../common/Input";
import { Select } from "../../../../common/Select";
import { Button, GeneralMessage } from "../../../../common/APICommon";
import { ConfirmationModal, ImageViewModal, PDFModal } from "../../../../common/Modal";
import { FEATURES } from "../../../../config/features";

const OvertimeRequestModule = ({
  user, formData, handleInputChange, handleFileChange,
  submitRequest, showMessage, message, isSuccess, loading
}) => {
  const fileInputRef = useRef(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  const otTypeOptions = [
    { value: "Regular", label: "Regular" },
    { value: "Restday", label: "Restday" },
    { value: "SpecialHoliday", label: "Special Holiday" },
    { value: "RegularHoliday", label: "Regular Holiday" },
    { value: "RestdaySpecialHoliday", label: "Restday Special Holiday" },
    { value: "RestdayRegularHoliday", label: "Restday Regular Holiday" },
  ];

  // // STEP 1: This ONLY validates and opens the modal. It does NOT call the API.
  const onFormSubmit = (e) => {
    if (e) e.preventDefault(); // HALT the form submission

    const errors = {};
    if (!formData.overtimeType) errors.overtimeType = true;
    if (!formData.dateRequested) errors.dateRequested = true;
    if (!formData.hours || parseFloat(formData.hours) <= 0) errors.hours = true;
    if (!formData.reason || formData.reason.length < 5) errors.reason = true;

    if (FEATURES.ALLOW_OT_REQUEST_DATETIME) {
      if (!formData.requestDateTimeFrom) errors.from = true;
      if (!formData.requestDateTimeTo) errors.to = true;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setErrorState(true);
      return;
    }

    setErrorState(false);
    setIsConfirmOpen(true); // // ONLY open the modal
  };

  // // STEP 2: This is the ONLY function that triggers the actual API call
  const handleFinalConfirm = async () => {
    const success = await submitRequest();
    if (success) {
      setIsConfirmOpen(false);
    }
  };

  const getFileUrl = () => {
    if (!formData.content) return "";
    return `data:${formData.fileType};base64,${formData.content}`;
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  return (
    <>
      <div className="space-y-6 p-4">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          {/* // Form onSubmit only triggers validation */}
          <form onSubmit={onFormSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Input label="Employee Name" type="text" value={user.employeePersonalInformationFullName} isDisabled={true} />

              <Select
                label="Overtime Type"
                value={formData.overtimeType}
                onChange={(e) => handleInputChange('overtimeType', e.target.value)}
                options={otTypeOptions}
                isRequired
                error={errorState && validationErrors.overtimeType}
              />

              <Input
                label="Date" type="date" value={formData.dateRequested}
                onChange={(e) => handleInputChange('dateRequested', e.target.value)} isRequired
                error={errorState && validationErrors.dateRequested}
              />

              {FEATURES.ALLOW_OT_REQUEST_DATETIME && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Request Date-Time From"
                    type="datetime-local"
                    value={formData.requestDateTimeFrom}
                    onChange={(e) => handleInputChange('requestDateTimeFrom', e.target.value)}
                    isRequired
                    error={errorState && validationErrors.from}
                  />
                  <Input
                    label="Request Date-Time To"
                    type="datetime-local"
                    value={formData.requestDateTimeTo}
                    onChange={(e) => handleInputChange('requestDateTimeTo', e.target.value)}
                    isRequired
                    error={errorState && validationErrors.to}
                  />
                </div>
              )}

              <Input
                label="Hours" type="number" value={formData.hours}
                onChange={(e) => handleInputChange('hours', e.target.value)}
                isRequired error={errorState && validationErrors.hours}
                isDisabled={FEATURES.ALLOW_OT_REQUEST_DATETIME}
              />

              <div className="flex flex-wrap gap-4 mt-2">
                <label className="flex items-center text-sm font-medium text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={formData.hasOvertimeAllowance} onChange={(e) => handleInputChange('hasOvertimeAllowance', e.target.checked)} className="mr-2" />
                  With Allowance?
                </label>
                <label className="flex items-center text-sm font-medium text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={formData.isAboveTwoHoursOvertime} onChange={(e) => handleInputChange('isAboveTwoHoursOvertime', e.target.checked)} className="mr-2" />
                  Above 2 Hours?
                </label>
                <label className="flex items-center text-sm font-medium text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={formData.isOTStraightWithoutBreak} onChange={(e) => handleInputChange('isOTStraightWithoutBreak', e.target.checked)} className="mr-2" />
                  Straight OT?
                </label>
              </div>
            </div>

            <div className="flex flex-col space-y-4">
              <TextArea label="Reason" placeholder="Enter your reason here..." isRequired value={formData.reason} onChange={(e) => handleInputChange('reason', e.target.value)} error={errorState && validationErrors.reason} rows={5} />
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center flex-grow flex flex-col justify-center transition-colors ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-50"}`}
                onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
              >
                <input type="file" ref={fileInputRef} className="hidden" accept=".jpg,.jpeg,.png,.pdf" onChange={(e) => handleFileChange(e.target.files[0])} />
                <p className="text-xs text-gray-500 mb-2">{formData.fileName ? <span className="text-blue-600 font-bold">Selected: {formData.fileName}</span> : "Drag & drop or Click to upload (Max 5MB)"}</p>
                <div className="flex justify-center gap-2">
                  <Button name="Upload File" className="bg-gray-500 text-white text-xs" onClick={() => fileInputRef.current.click()} />
                  {formData.content && <Button name="Preview" className="bg-blue-500 text-white text-xs" onClick={() => setIsPreviewOpen(true)} />}
                </div>
              </div>
            </div>

            <div className="md:col-span-2 flex justify-end">
              {/* // Button type="submit" will trigger onFormSubmit */}
              <Button name="Send Request" className="bg-blue-600 text-white px-8 py-2.5 font-bold" type="submit" />
            </div>
          </form>
        </div>
      </div>

      {formData.fileType === "application/pdf" ? (
        <PDFModal visible={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} fileUrl={getFileUrl()} />
      ) : (
        <ImageViewModal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} fileData={formData.content} fileType={formData.fileType || "image/jpeg"} />
      )}

      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />

      <ConfirmationModal
        visible={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleFinalConfirm} // // MODIFIED: Call local guard function
        title="Confirm Overtime Submission"
        message={`Confirming OT Request for ${formData.hours} hour(s)?`}
        isLoading={loading}
      />
    </>
  );
};

export default OvertimeRequestModule;